import React, { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { gtplSaathiAPI } from '../../services/api';

// "6/12-month box swap & order" on the customer's GTPL Saathi tab — the owner's by-hand 6/12-month
// process, run by the server (backend services/gtplLongTermService.js; design in
// docs/superpowers/specs/2026-09-28-gtpl-long-term-box-swap-design.md). Preview is read-only and
// runs in the background (it drives several GTPL pages), so this polls the customer's runs while
// anything is in progress.

const STEPS = [
  { key: 'wallet', label: 'Wallet check' },
  { key: 'swap1', label: 'Swap 1 — customer account gets the spare box' },
  { key: 'swap2', label: 'Swap 2 — 93-day account gets the customer\'s box' },
  { key: 'order', label: 'Package order' },
  { key: 'aastha', label: 'AASTHA à-la-carte' },
  { key: 'crm', label: 'CRM update' },
  { key: 'gtplSync', label: 'GTPL sync — new account gets the CRM name, address & mobile' },
];
const ACTIVE = ['previewing', 'running', 'waiting_wallet'];
const STUCK_MS = 10 * 60 * 1000; // matches the server's stale-run window
const IDLE_REFRESH_MS = 30000; // an automatic run can appear while this tab is open (a payment is collected)
const inr = (n) => new Intl.NumberFormat('en-IN').format(Math.round(n || 0));
const fmtTimeSec = (d) => (d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '');
const fmtTime = (d) => (d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');

function PlanTable({ plan, walletBalance }) {
  const enough = walletBalance >= plan.price;
  const rows = [
    ...(plan.customerAddress ? [['Customer address', plan.customerAddress]] : []),
    ['Customer account (111)', `${plan.oldAccount} · box ${plan.customerBox} · ${plan.customerEntity}`],
    plan.spareSource === 'stock'
      ? ['Spare box (GTPL stock)', `${plan.spareBox} · ${plan.spareModel || ''} · ${plan.spareEntity} · blank box, not on any account`]
      : ['Spare box (Pending)', `${plan.spareBox} · from account ${plan.spareAccount} · ${plan.spareEntity}`],
    ['93-day account (333)', `${plan.targetAccount} · box ${plan.targetOldBox} · ${plan.targetEntity} · inactive ${plan.targetInactiveDays} days`],
    ['Order', `${plan.packageName} · ${{ D90: '3 months (D90)', D180: '6 months (D180)', D360: '12 months (D360)' }[plan.validity] || plan.validity} + AASTHA`],
    ['Price', `₹${inr(plan.price)} from ${plan.targetEntity}`],
    ['Wallet available', `₹${inr(walletBalance)}`],
    ['Wallet required', `₹${inr(plan.price)}`],
    ...(enough ? [] : [['Add to wallet', `₹${inr(plan.price - walletBalance)} — the swap waits for this top-up`]]),
  ];
  return (
    // Label above value on a phone, side by side when there is room (a nowrap table overflowed).
    <div style={{ fontSize: '.84rem', margin: '10px 0', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
      {rows.map(([k, v], i) => (
        <div key={k} style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 12px', padding: '8px 12px', borderTop: i ? '1px solid var(--border)' : 'none' }}>
          <span style={{ color: 'var(--text-muted)', flex: '0 0 190px', maxWidth: '100%' }}>{k}</span>
          <span style={{ fontWeight: 600, flex: '1 1 200px', minWidth: 0, wordBreak: 'break-word', color: k.startsWith('Wallet') || k === 'Add to wallet' ? (enough ? 'var(--success)' : 'var(--danger, #dc2626)') : undefined }}>{v}</span>
        </div>
      ))}
    </div>
  );
}

function StepList({ run }) {
  const at = STEPS.findIndex((s) => s.key === run.step);
  return (
    <ol style={{ listStyle: 'none', padding: 0, margin: '10px 0', fontSize: '.85rem' }}>
      {STEPS.map((s, i) => {
        const log = [...(run.log || [])].reverse().find((l) => l.step === s.key);
        let icon = '○';
        if (run.status === 'done' || i < at) icon = '✅';
        else if (i === at && run.status === 'failed') icon = '❌';
        else if (i === at && run.status === 'waiting_wallet') icon = '💰';
        else if (i === at && run.status === 'running') icon = '⏳';
        return (
          <li key={s.key} style={{ padding: '4px 0', display: 'flex', gap: 8 }}>
            <span style={{ width: 20 }}>{icon}</span>
            <span>
              <b>{s.label}</b>
              {log && <span style={{ display: 'block', color: 'var(--text-muted)', fontSize: '.78rem' }}>{log.at && <b>{fmtTimeSec(log.at)} · </b>}{log.message}</span>}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

export default function GtplLongTermSwapSection({ customer }) {
  const [months, setMonths] = useState(12);
  const [runs, setRuns] = useState([]);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await gtplSaathiAPI.longTermRuns(customer._id);
      setRuns(data.data || []);
    } catch { /* keep the last list on a transient error */ }
  }, [customer._id]);

  useEffect(() => { load(); }, [load]);
  const latest = runs[0];
  // An automatic run (started by a 6/12-month payment) sits 'scheduled' until the sweep picks it
  // up, then goes previewing → ready → Start on its own — keep polling through all of that.
  const auto = !!latest?.autoStart;
  const scheduled = latest?.status === 'scheduled';
  const autoReady = auto && latest?.status === 'ready';
  const active = latest && (ACTIVE.includes(latest.status) || scheduled || autoReady);
  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(load, active ? 5000 : IDLE_REFRESH_MS);
    return () => clearTimeout(timer.current);
  }, [active, runs, load]);

  const preview = async () => {
    setBusy(true);
    try {
      await gtplSaathiAPI.longTermPreview({ customerId: customer._id, months });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Preview failed');
    } finally {
      setBusy(false);
    }
  };

  const start = async (run) => {
    const p = run.plan;
    if (!window.confirm(`Start the ${run.months}-month box swap?\n\n• Account ${p.oldAccount} gets spare box ${p.spareBox}${p.spareSource === 'stock' ? ` (blank stock box, ${p.spareEntity})` : ''}\n• Account ${p.targetAccount} gets box ${p.customerBox}\n• ${p.packageName} ${p.validity} + AASTHA on ${p.targetAccount} (₹${inr(p.price)} from ${p.targetEntity})\n• CRM account ${p.oldAccount} → ${p.targetAccount}\n\nThis changes real boxes and spends real money on GTPL Saathi.`)) return;
    setBusy(true);
    try {
      await gtplSaathiAPI.longTermStart(run._id);
      toast.success('Started — watch the steps below');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not start');
    } finally {
      setBusy(false);
    }
  };

  // Start again from the step a run stopped on (or hung at). The failed step may already have
  // reached GTPL before it errored, so staff confirm after checking gtplsaathi.com.
  const resume = async (run) => {
    const stepLabel = STEPS.find((x) => x.key === run.step)?.label;
    const msg = run.plan?.oldAccount
      ? `Start again from "${stepLabel || run.step}"?\n\nThe step that stopped may already have gone through on GTPL Saathi before it errored — check gtplsaathi.com first (box on the account, order placed) so nothing is done or charged twice.`
      : 'Start this automatic swap again?';
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      await gtplSaathiAPI.longTermResume(run._id);
      toast.success('Started again — watch the steps below');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not start again');
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (run) => {
    setBusy(true);
    try {
      await gtplSaathiAPI.longTermCancel(run._id);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not cancel');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ padding: 16, marginBottom: 20 }}>
      <h3 style={{ margin: '0 0 6px', fontSize: '1rem', fontWeight: 600 }}>3/6/12-month box swap &amp; order</h3>
      <p style={{ fontSize: '.8rem', color: 'var(--text-muted)', margin: '0 0 12px' }}>
        Moves this customer's box onto an account inactive 93+ days and orders the long plan there, plus AASTHA.
        Preview changes nothing; Start does the swaps and spends from the wallet.
      </p>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <select className="form-control" value={months} onChange={(e) => setMonths(Number(e.target.value))} disabled={busy || active} style={{ width: 'auto' }}>
          <option value={3}>3 months</option>
          <option value={6}>6 months</option>
          <option value={12}>12 months</option>
        </select>
        <button type="button" className="btn btn-secondary" onClick={preview} disabled={busy || active}>
          {latest?.status === 'previewing' && !auto ? 'Preparing preview… (about a minute)' : 'Preview'}
        </button>
      </div>

      {scheduled && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: '.82rem', fontWeight: 700 }}>
            Automatic {latest.months}-month swap scheduled — starts {fmtTime(latest.runAfter)} or when the box-swap slot is free
          </div>
          <ol style={{ margin: '8px 0', paddingLeft: 18, fontSize: '.78rem', color: 'var(--text-muted)' }}>
            {(latest.log || []).map((l, i) => <li key={i}>{l.at && <b>{fmtTimeSec(l.at)} · </b>}{l.message}</li>)}
          </ol>
          <button type="button" className="btn btn-secondary" onClick={() => cancel(latest)} disabled={busy}>Cancel (nothing changed yet)</button>
        </div>
      )}

      {auto && (latest.status === 'previewing' || latest.status === 'ready') && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: '.82rem', fontWeight: 700 }}>Automatic {latest.months}-month swap — {latest.status === 'previewing' ? 'preparing the plan…' : 'starting…'}</div>
          {latest.status === 'ready' && latest.plan?.oldAccount && <PlanTable plan={latest.plan} walletBalance={latest.walletBalance} />}
        </div>
      )}

      {latest && latest.status === 'ready' && !auto && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: '.82rem', fontWeight: 700 }}>Preview — {latest.months} months ({fmtTime(latest.updatedAt)})</div>
          <PlanTable plan={latest.plan} walletBalance={latest.walletBalance} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn btn-danger" title="Changes real boxes and spends real wallet money" onClick={() => start(latest)} disabled={busy}>Start (spends wallet)</button>
            <button type="button" className="btn btn-secondary" onClick={() => cancel(latest)} disabled={busy}>Discard</button>
          </div>
        </div>
      )}

      {latest && ['running', 'waiting_wallet', 'done', 'failed'].includes(latest.status) && latest.plan?.oldAccount && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: '.88rem', fontWeight: 700, color: { done: 'var(--success)', failed: 'var(--danger, #dc2626)', waiting_wallet: 'var(--warning, #d97706)', running: 'var(--info, #2563eb)' }[latest.status] }}>
            {auto && <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>Automatic (from payment) · </span>}
            {latest.status === 'done' && `Done — account ${latest.plan.oldAccount} → ${latest.plan.targetAccount}`}
            {latest.status === 'running' && 'Running…'}
            {latest.status === 'waiting_wallet' && latest.step !== 'wallet' && `Retrying “${STEPS.find((x) => x.key === latest.step)?.label || latest.step}” automatically every minute (try ${latest.stepRetries || 0})`}
            {latest.status === 'waiting_wallet' && latest.step === 'wallet' && `Waiting for ${latest.plan.targetEntity} wallet: ₹${inr(latest.walletBalance)} available, ₹${inr(latest.plan.price)} required — add ₹${inr(latest.plan.price - latest.walletBalance)}; continues by itself after a top-up`}
            {latest.status === 'failed' && `Stopped: ${latest.failMessage}`}
          </div>
          <div style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>
            {latest.startedAt ? `Started ${fmtTimeSec(latest.startedAt)}` : `Created ${fmtTimeSec(latest.createdAt)}`}
            {latest.completedAt ? ` · Finished ${fmtTimeSec(latest.completedAt)}` : ''}
          </div>
          <StepList run={latest} />
          {latest.status === 'waiting_wallet' && latest.step === 'wallet' && (
            <button type="button" className="btn btn-secondary" onClick={() => cancel(latest)} disabled={busy}>Cancel (nothing changed yet)</button>
          )}
          {latest.status === 'failed' && latest.step !== 'done' && (
            <button type="button" className="btn btn-primary" onClick={() => resume(latest)} disabled={busy}>
              Start again from “{STEPS.find((x) => x.key === latest.step)?.label || latest.step}”
            </button>
          )}
          {latest.status === 'running' && Date.now() - new Date(latest.updatedAt).getTime() > STUCK_MS && (
            <button type="button" className="btn btn-primary" onClick={() => resume(latest)} disabled={busy}>
              Nothing has moved for 10+ minutes — start again
            </button>
          )}
        </div>
      )}

      {latest && latest.status === 'failed' && !latest.plan?.oldAccount && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: '.84rem', color: 'var(--danger, #dc2626)' }}>{auto ? 'Automatic swap stopped before starting' : 'Preview refused'}: {latest.failMessage}</div>
          {auto && <button type="button" className="btn btn-primary" style={{ marginTop: 8 }} onClick={() => resume(latest)} disabled={busy}>Start again</button>}
        </div>
      )}
    </div>
  );
}
